import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { Crown, Star, Check, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { API_BASE_URL } from "../config/apiConfig";

const pricingPlans = [
    // {
    //     name: "Premium",
    //     price: { monthly: 4500, annual: 40000 },
    //     description: "Most popular choice for serious students",
    //     icon: <Star className="w-6 h-6" />,
    //     color: "from-blue-500 to-blue-600",
    //     popular: true,
    //     features: [
    //         "Access to all courses",
    //         "Study materials & notes",
    //         "Practice tests",
    //         "Email support",
    //         "Mobile app access"
    //     ]
    // },
    {
        name: "Premium Plus",
        price: { monthly: 7000, annual: 70000 },
        description: "Ultimate package with exclusive features",
        icon: <Crown className="w-6 h-6" />,
        color: "from-purple-500 to-purple-600",
        popular: false,
        features: [
            "Everything in Premium",
            "Priority support",
            "Exclusive webinars",
            "1-on-1 tutoring sessions",
            "Advanced analytics",
            "Downloadable content"
        ]
    }
];

const SubscriptionGate = () => {
    const [billingCycle, setBillingCycle] = useState("monthly");
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        checkSubscriptionStatus();
    }, []);

    const checkSubscriptionStatus = async () => {
        try {
            const token = localStorage.getItem("token");

            if (!token) {
                setIsLoading(false);
                return;
            }

            const res = await fetch(`${API_BASE_URL}/api/sub/single`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = await res.json();
            const activeSub = data.subscriptions?.find(
                (sub) => sub.status === "Active"
            );

            if (activeSub) {
                navigate("/dashboard");
                return;
            }

            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setIsLoading(false);
        }
    };

    const handlePlanSelect = (plan) => setSelectedPlan(plan);

    const proceedToPayment = async () => {
        if (!selectedPlan) {
            toast.error("Please select a plan first");
            return;
        }

        setIsProcessing(true);

        try {
            const token = localStorage.getItem("token");

            if (!token) {
                toast.error("Please login to continue");
                navigate("/login");
                return;
            }

            const decoded = jwtDecode(token);
            const userId = decoded?.id;
            const userEmail = decoded?.email;

            const amount = selectedPlan.price[billingCycle];

            const res = await fetch(`${API_BASE_URL}/api/sub/paystack/${userId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ email: userEmail, amount })
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Failed to initialize payment");
            }

            // Redirect to Paystack
            window.location.href = data.payment_url;
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Failed to initialize payment");
            setIsProcessing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Checking subscription status...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <Toaster position="top-right" richColors />

            <div className="min-h-screen font-montserrat bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">

                    {/* HEADER */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-6 shadow-xl">
                            <span className="text-white font-bold text-3xl">T</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Subscribe to Continue
                        </h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Choose a plan to unlock all premium features and start your learning journey with Teechaa
                        </p>
                    </div>

                    {/* BILLING TOGGLE */}
                    <div className="flex justify-center mb-8">
                        <div className="inline-flex items-center gap-2 bg-white p-1.5 rounded-xl shadow-md">
                            {["monthly", "annual"].map((cycle) => (
                                <button
                                    key={cycle}
                                    className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all ${billingCycle === cycle
                                            ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
                                            : "text-gray-600 hover:text-gray-800"
                                        }`}
                                    onClick={() => setBillingCycle(cycle)}
                                >
                                    {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                                    {cycle === "annual" && (
                                        <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                            Save 16.7%
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* PLAN CARDS */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                        {pricingPlans.map((plan) => (
                            <div
                                key={plan.name}
                                className={`relative bg-white rounded-2xl p-8 transition-all duration-300 cursor-pointer ${selectedPlan?.name === plan.name
                                        ? "ring-4 ring-blue-500 shadow-2xl scale-105"
                                        : "shadow-lg hover:shadow-xl hover:scale-102"
                                    }`}
                                onClick={() => handlePlanSelect(plan)}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                        <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg">
                                            ⭐ Most Popular
                                        </span>
                                    </div>
                                )}

                                <div className="flex items-start gap-4 mb-6">
                                    <div className={`p-3 rounded-xl bg-gradient-to-r ${plan.color} text-white shadow-lg`}>
                                        {plan.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                                        <p className="text-sm text-gray-600">{plan.description}</p>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-5xl font-bold text-gray-900">
                                            ₦{plan.price[billingCycle].toLocaleString()}
                                        </span>
                                        <span className="text-gray-500">/ {billingCycle}</span>
                                    </div>

                                    {billingCycle === "annual" && (
                                        <p className="text-sm text-green-600 font-medium mt-2">
                                            Save ₦{(
                                                plan.price.monthly * 12 -
                                                plan.price.annual
                                            ).toLocaleString()}
                                        </p>
                                    )}
                                </div>

                                <ul className="space-y-3 mb-8">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                            <span className="text-gray-700">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div
                                    className={`w-full py-3 rounded-xl font-semibold text-center transition-all ${selectedPlan?.name === plan.name
                                            ? "bg-blue-600 text-white shadow-lg"
                                            : "bg-gray-100 text-gray-400"
                                        }`}
                                >
                                    {selectedPlan?.name === plan.name
                                        ? "✓ Selected"
                                        : "Select Plan"}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* BUTTON */}
                    <div className="max-w-md mx-auto">
                        <button
                            className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            onClick={proceedToPayment}
                            disabled={!selectedPlan || isProcessing}
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                "Continue to Payment"
                            )}
                        </button>

                        <p className="text-center text-sm text-gray-500 mt-4">
                            Secure payment powered by Paystack
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SubscriptionGate;
